import { Dialog, DialogContent } from "@/components/ui/dialog";
import { InfoIcon } from "lucide-react";
import { BonusProgress } from "@/components/ui/bonus-progress";
import { DateTime } from "luxon";

interface PayoutModalProps {
  open: boolean;
  onClose: () => void;
  incentiveData: any;
  currentMonth: string;
  caseload: number;
}

export function PayoutModal({
  open,
  onClose,
  incentiveData,
  caseload,
  currentMonth,
}: PayoutModalProps) {
  const currentMonthIncentives = incentiveData?.results?.[0];
  const currentDate = DateTime.now();
  const year = currentDate.year;

  const BASELINE_TEENS = 21;
  const ENGAGEMENT_BONUS_PER_TEEN = 60;

  const eligibleTeens = currentMonthIncentives?.totalMonthlyEligibleTeens;
  const progressAboveBaseline = Math.max(eligibleTeens - BASELINE_TEENS + 1, 0);

  const formatMonth = (month: string) => {
    const date = DateTime.fromFormat(`${month} 1, ${year}`, "MMM d, yyyy");
    return date.toFormat("MMMM");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="ml-[50px] max-h-[90vh] max-w-[90vw] overflow-y-auto bg-white p-4 md:p-6">
        <div className="space-y-6">
          {/* Monthly Bonus Section */}
          <div>
            <h2 className="font-['TWK Lausanne'] mb-2 text-lg font-normal text-gray-400">
              {formatMonth(currentMonth)} {year}
            </h2>
            <h3 className="font-['TWK Lausanne'] mb-4 text-2xl font-bold text-gray-900">
              Monthly bonus
            </h3>

            <div className="mb-4 text-3xl text-gray-900">
              ${currentMonthIncentives?.incentiveBonus || 0}
            </div>

            <div className="pt-4">
              <div className="mb-4 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div className="w-full md:w-1/2">
                  <BonusProgress
                    totalMonthlyEligibleTeens={
                      currentMonthIncentives?.totalMonthlyEligibleTeens
                    }
                    caseload={caseload}
                  />
                </div>

                <div className="w-full text-left md:w-1/2">
                  <h4 className="mb-2text-sm font-medium text-gray-400">
                    Payout Breakdown
                  </h4>
                  <div className="text-sm">
                    <div className="mb-1 flex items-center gap-2">
                      <span>
                        Engagement Bonus ({progressAboveBaseline} x $
                        {ENGAGEMENT_BONUS_PER_TEEN})
                      </span>
                      <span className="text-green-600">
                        +${progressAboveBaseline * ENGAGEMENT_BONUS_PER_TEEN}
                      </span>
                    </div>
                    <div className="mt-32 mt-4 flex items-center gap-1 text-xs text-gray-500">
                      <InfoIcon className="h-5 w-5" />
                      <span>
                        Base pay not shown - this reflects only your bonus based
                        on teen engagement.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Bonus Section */}
          <div className="mt-8">
            <h4 className="font-['TWK Lausanne'] mb-2 text-lg font-light text-gray-400">
              Historical bonus
            </h4>
            <div className="rounded">
              <div className="font-['TWK Lausanne'] flex justify-between border-b border-gray-200 px-4 py-2 text-sm text-gray-500">
                <span>Month</span>
                <span>Payout</span>
              </div>
              {incentiveData?.results?.slice(1).map((item: any) => (
                <div
                  key={item.month}
                  className="font-['TWK Lausanne'] flex justify-between border-b border-gray-50 px-4 py-2 last:border-b-0"
                >
                  <span>{formatMonth(item.month)}</span>
                  <span className="font-bold text-gray-900">
                    ${item.incentiveBonus || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
